import type { QueryClient } from '@tanstack/react-query';
import type { Session } from '@/api/client';
import { collectTableProjectIds } from '@/lib/summariesBatchReload';
import { getProjectEntityFromCaches } from '@/lib/projectEntityCache';

/** Состояния проектов, для которых сервер считает plannerStatus в project-resource-summaries. */
const PLANNER_BADGE_PROJECT_STATES = new Set(['Добавлен', 'Запись']);

export type SessionSnapshotForRowSummaries = Pick<
  Session,
  'project_id' | 'session_type' | 'reader_ids' | 'readers' | 'reader_id'
>;

export type SessionDeletedRealtimePayload = {
  id: string;
  project_id?: string | null;
  reader_ids?: string[];
  session_type?: string | null;
};

export type SessionSummariesAccumulateContext =
  | { kind: 'created' }
  | { kind: 'updated'; previousProjectId?: string | null }
  | { kind: 'deleted'; deletedProjectId: string | null };

function extractSessionReaderPersonIds(session: SessionSnapshotForRowSummaries): string[] {
  const fromReaders = session.readers?.map((r) => r.id).filter(Boolean) ?? [];
  const fromIds = session.reader_ids?.filter(Boolean) ?? [];
  const merged = [...fromReaders, ...fromIds];
  if (session.reader_id) merged.push(session.reader_id);
  return [...new Set(merged)];
}

/** Последнее известное project_id по кэшам расписания (до патча WS). */
export function findCachedSessionProjectId(
  queryClient: QueryClient,
  sessionId: string
): string | null | undefined {
  const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
  for (const [, old] of entries) {
    if (!old) continue;
    const found = old.find((s) => s.id === sessionId);
    if (found) return found.project_id ?? null;
  }
  return undefined;
}

function unassignedPoolTouched(ctx: SessionSummariesAccumulateContext, newPid: string | null): boolean {
  if (ctx.kind === 'deleted') {
    return ctx.deletedProjectId === null;
  }
  if (ctx.kind === 'created') {
    return newPid === null;
  }
  const oldPid = ctx.previousProjectId;
  if (oldPid === undefined) return true;
  if (newPid === null) return true;
  if (oldPid === null) return true;
  return false;
}

/**
 * Свободные сессии «запись» (без проекта) входят в пул планера для всех одночтецовых строк «Добавлен»/«Запись».
 * Инвалидируем их только когда сессия попала в пул / вышла из пула / удалена из пула.
 */
export function accumulateProjectIdsForSessionRowSummaries(
  queryClient: QueryClient,
  target: Set<string>,
  session: SessionSnapshotForRowSummaries,
  ctx: SessionSummariesAccumulateContext
): void {
  if (session.project_id) target.add(session.project_id);
  const st = String(session.session_type ?? 'запись').trim();
  if (st !== 'запись') return;

  const newPid = session.project_id ?? null;
  if (!unassignedPoolTouched(ctx, newPid)) return;

  const readerPersonIds = extractSessionReaderPersonIds(session);
  if (readerPersonIds.length === 0) return;

  const rs = new Set(readerPersonIds);
  for (const pid of collectTableProjectIds(queryClient)) {
    const p = getProjectEntityFromCaches(queryClient, pid);
    if (!p || !PLANNER_BADGE_PROJECT_STATES.has(String(p.state))) continue;
    const readers = p.readers ?? [];
    if (readers.length !== 1) continue;
    const rid = readers[0]?.id;
    if (rid && rs.has(rid)) target.add(pid);
  }
}

export function sessionSnapshotFromDeletedRealtimePayload(
  p: SessionDeletedRealtimePayload
): SessionSnapshotForRowSummaries {
  return {
    project_id: p.project_id ?? null,
    reader_ids: p.reader_ids,
    session_type: (p.session_type ?? 'запись') as Session['session_type'],
    reader_id: p.reader_ids?.[0] ?? null,
  };
}
